/*
  Carry-on compare chart (sections/carry-on-compare.liquid).

  1. "Change line": below 1280px the chart shows 4, 3 or 2 lines (CSS hides the rest by position in the
     row). Each column has a disclosure button with a list of line buttons (not a <select>, whose change
     event fires on every arrow key on Windows). Choosing a line swaps the two lines' cells in every row,
     so the chosen line takes that column. Focus returns to that column's button and a polite live region
     says what changed. Escape or a click elsewhere closes the list.
  2. "Show differences only": marks rows where the lines on screen all read the same (data-same="1"),
     and a whole group when all of its rows match; CSS hides them while the box is ticked. The box is only
     offered while some lines are off screen: when every line fits (1280px and up, or a chart with no more
     lines than the breakpoint holds) the root gets is-all-lines, CSS hides the box and diff mode is off,
     so every row shows. The box keeps its ticked state while hidden, so narrowing again restores it.
  3. Header text heights: the line names and carry-on labels of the columns on screen get the height of
     the tallest one, so the prices sit on one line across the chart.
  Without JavaScript the chart still shows the first lines in brand order with their real prices.
*/
(() => {
  const WIDE = '(min-width: 1280px)';
  const queries = [
    [window.matchMedia('(max-width: 749px)'), 2],
    [window.matchMedia('(max-width: 989px)'), 3],
    [window.matchMedia('(max-width: 1279px)'), 4],
  ];

  function visibleCount(total) {
    for (const [query, count] of queries) {
      if (query.matches) return Math.min(count, total);
    }
    return total;
  }

  function cellText(cell) {
    return cell ? cell.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : '';
  }

  function init(root) {
    if (root.dataset.ccReady) return;
    root.dataset.ccReady = '1';

    const table = root.querySelector('.cc-table');
    if (!table || !table.tHead) return;

    const headRow = table.tHead.rows[0];
    const columns = Array.from(headRow.querySelectorAll(':scope > th[data-col]')).map((th) => th.dataset.col);
    const names = {};
    headRow.querySelectorAll(':scope > th[data-col]').forEach((th) => {
      names[th.dataset.col] = th.dataset.name || '';
    });
    const rows = Array.from(table.rows).filter((row) => row.querySelector(':scope > [data-col]'));
    const specRows = Array.from(table.querySelectorAll('tr[data-spec]'));
    const status = root.querySelector('[data-cc-status]');
    const diffToggle = root.querySelector('[data-cc-diff]');

    let order = columns.slice();

    function place() {
      rows.forEach((row) => {
        const cells = {};
        row.querySelectorAll(':scope > [data-col]').forEach((cell) => {
          cells[cell.dataset.col] = cell;
        });
        order.forEach((col) => {
          if (cells[col]) row.appendChild(cells[col]);
        });
      });
    }

    function markSame() {
      const shown = order.slice(0, visibleCount(order.length));
      specRows.forEach((row) => {
        const values = shown.map((col) => cellText(row.querySelector(`:scope > [data-col="${col}"]`)));
        const same = values.length > 1 && values.every((value) => value === values[0]) ? '1' : '0';
        row.dataset.same = same;
        const labelRow = table.querySelector(`tr[data-label-for="${row.dataset.spec}"]`);
        if (labelRow) labelRow.dataset.same = same;
      });
      table.querySelectorAll('tbody.cc-group').forEach((group) => {
        const groupRows = Array.from(group.querySelectorAll('tr[data-spec]'));
        group.dataset.same = groupRows.length && groupRows.every((row) => row.dataset.same === '1') ? '1' : '0';
      });
    }

    const textParts = ['.cc-head__name', '.cc-head__product'];
    function equalize() {
      const shown = order.slice(0, visibleCount(order.length));
      textParts.forEach((selector) => {
        const all = Array.from(headRow.querySelectorAll(`th[data-col] ${selector}`));
        all.forEach((el) => {
          el.style.minHeight = '0px';
        });
        const heights = shown
          .map((col) => headRow.querySelector(`th[data-col="${col}"] ${selector}`))
          .filter(Boolean)
          .map((el) => el.getBoundingClientRect().height);
        // At least 24 px: the name and carry-on links are tap targets (WCAG 2.5.8). On phones both are one
        // line (18 to 19 px) and sit 2 px apart, which fails the target-size minimum.
        const tallest = Math.max(24, Math.ceil(Math.max(0, ...heights)));
        all.forEach((el) => {
          el.style.minHeight = `${tallest}px`;
        });
      });
    }

    function swap(fromCol, toCol) {
      const i = order.indexOf(fromCol);
      const j = order.indexOf(toCol);
      if (i < 0 || j < 0 || i === j) return;
      order[i] = toCol;
      order[j] = fromCol;
      place();
      markSame();
      equalize();

      const count = visibleCount(order.length);
      if (status) {
        const replaced = j < count ? ` and ${names[fromCol]} moved to column ${j + 1}` : '';
        status.textContent = `Column ${i + 1} now shows ${names[toCol]}${replaced}. Showing ${order
          .slice(0, count)
          .map((col) => names[col])
          .join(', ')}.`;
      }
    }

    const pickButtons = Array.from(headRow.querySelectorAll('[data-cc-pick]'));
    const listFor = (button) => document.getElementById(button.getAttribute('aria-controls'));

    function closePicks(except) {
      pickButtons.forEach((button) => {
        if (button === except) return;
        button.setAttribute('aria-expanded', 'false');
        const list = listFor(button);
        if (list) list.hidden = true;
      });
    }

    pickButtons.forEach((button) => {
      const list = listFor(button);
      if (!list) return;

      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        closePicks(button);
        button.setAttribute('aria-expanded', String(open));
        list.hidden = !open;
      });

      list.addEventListener('click', (event) => {
        const option = event.target.closest('[data-cc-choose]');
        if (!option) return;
        const fromCol = button.closest('th').dataset.col;
        const toCol = option.dataset.ccChoose;
        closePicks();
        if (toCol !== fromCol) swap(fromCol, toCol);
        const target = headRow.querySelector(`th[data-col="${toCol}"] [data-cc-pick]`);
        if (target) target.focus();
      });
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = pickButtons.find((button) => button.getAttribute('aria-expanded') === 'true');
      if (!open) return;
      closePicks();
      open.focus();
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.cc-head__pick')) closePicks();
    });

    // Diff mode applies only while the box is offered (some lines off screen) and ticked
    function syncDiff() {
      const allLines = visibleCount(order.length) >= order.length;
      root.classList.toggle('is-all-lines', allLines);
      root.classList.toggle('is-diff-only', Boolean(diffToggle && diffToggle.checked) && !allLines);
    }

    if (diffToggle) diffToggle.addEventListener('change', syncDiff);
    syncDiff();

    const onBreakpoint = () => {
      closePicks();
      if (window.matchMedia(WIDE).matches && order.join() !== columns.join()) {
        order = columns.slice();
        place();
      }
      syncDiff();
      markSame();
      equalize();
    };
    queries.forEach(([query]) => query.addEventListener('change', onBreakpoint));
    window.matchMedia(WIDE).addEventListener('change', onBreakpoint);

    // Column widths change with the window inside a breakpoint too, and so does the wrapping
    let lastWidth = window.innerWidth;
    let frame = 0;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(equalize);
    });

    markSame();
    equalize();
    root.classList.add('is-ready');
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(equalize);
  }

  function initAll() {
    document.querySelectorAll('[data-cc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
